import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ErrorBanner } from '@/components/ErrorBanner';
import { PageHeader } from '@/components/PageHeader';
import { Spinner } from '@/components/Spinner';
import { describeError } from '@/lib/api';
import { sessionTitle } from '@/components/workout/format';
import { DISCARD_SESSION } from '@/lib/i18n/training';
import { isEmptyOpenSession } from '@/lib/offline/discard';
import { discardSession, finishSession, startSession } from '@/lib/offline/session';
import { useActiveSession, useLocalSets, useRoutine } from '@/lib/queries/workouts';

/**
 * Parámetros de arranque. Los enlaza el detalle de rutina
 * (`/train/session/new?routineDayId=…&routineId=…`) y también la portada de
 * Entrenar; sin ninguno de los dos es un entrenamiento libre.
 */
const searchSchema = z.object({
  routineId: z.uuid().optional(),
  routineDayId: z.uuid().optional(),
});

export const Route = createFileRoute('/_app/train/session/new')({
  validateSearch: (search: Record<string, unknown>) => searchSchema.parse(search),
  component: NewSessionPage,
});

function NewSessionPage() {
  const { routineId, routineDayId } = Route.useSearch();
  const navigate = useNavigate();
  const active = useActiveSession();
  const routine = useRoutine(routineId ?? null);
  const activeSets = useLocalSets(active?.clientId ?? '');
  // La abierta no tiene series: cerrarla dejaría un entreno vacío, se descarta.
  const activeEmpty = activeSets !== undefined && isEmptyOpenSession(active, activeSets.length);
  const started = useRef(false);
  const [error, setError] = useState<string | null>(null);

  // Continuar la sesión abierta si es exactamente la que se pedía: volver atrás
  // o tocar dos veces «Empezar» no debe crear entrenamientos duplicados.
  const sameAsActive =
    active != null &&
    (active.routineDayId ?? undefined) === routineDayId &&
    active.finishedAt === null;

  const routineReady = routineId === undefined || routine.isSuccess;

  useEffect(() => {
    if (active === undefined) return;
    if (active !== null && sameAsActive) {
      void navigate({
        to: '/train/session/$clientId',
        params: { clientId: active.clientId },
        replace: true,
      });
      return;
    }
    // El guardia va dentro del efecto y no en una condición calculada al
    // renderizar: en modo estricto React monta, desmonta y vuelve a montar sin
    // renderizar en medio, y una bandera de render no llegaría a enterarse.
    if (started.current || active !== null || !routineReady) return;
    started.current = true;
    void (async () => {
      try {
        const session = await startSession({
          routine: routine.data ?? null,
          routineDayId: routineDayId ?? null,
        });
        await navigate({
          to: '/train/session/$clientId',
          params: { clientId: session.clientId },
          replace: true,
        });
      } catch (cause) {
        started.current = false;
        setError(describeError(cause));
      }
    })();
  }, [active, sameAsActive, routineReady, navigate, routine.data, routineDayId]);

  async function replaceActive(): Promise<void> {
    if (active == null) return;
    if (activeEmpty) await discardSession(active.clientId);
    else await finishSession(active.clientId);
  }

  if (error !== null) {
    return (
      <>
        <PageHeader title="Empezar entrenamiento" />
        <ErrorBanner title="No se ha podido crear la sesión" message={error} />
      </>
    );
  }

  // Ya hay un entrenamiento abierto y no es este: se pregunta en vez de
  // arrastrar dos sesiones a medias o cerrar la anterior por la cara.
  if (active != null && !sameAsActive) {
    return (
      <>
        <PageHeader title="Ya estás entrenando" />
        <Card
          title={sessionTitle(active.routineName, active.dayName)}
          description="Tienes un entrenamiento sin terminar. Puedes seguir con él o cerrarlo y empezar el nuevo."
        >
          <div className="flex flex-col gap-2">
            <Button
              size="lg"
              block
              onClick={() =>
                void navigate({
                  to: '/train/session/$clientId',
                  params: { clientId: active.clientId },
                  replace: true,
                })
              }
            >
              Continuar con el actual
            </Button>
            <Button size="lg" block variant="secondary" onClick={() => void replaceActive()}>
              {activeEmpty ? DISCARD_SESSION.replace : 'Terminarlo y empezar este'}
            </Button>
          </div>
        </Card>
      </>
    );
  }

  if (routineId !== undefined && routine.isError) {
    return (
      <>
        <PageHeader title="Empezar entrenamiento" />
        <div className="flex flex-col gap-3">
          <ErrorBanner
            title="No hemos podido cargar la rutina"
            error={routine.error}
            onRetry={() => void routine.refetch()}
          />
          <Button
            size="lg"
            block
            variant="secondary"
            onClick={() => void navigate({ to: '/train/session/new', search: {}, replace: true })}
          >
            Empezar sin rutina
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Preparando tu entrenamiento" subtitle="Un segundo…" />
      <div className="flex justify-center py-10">
        <Spinner size="lg" />
      </div>
    </>
  );
}
